import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, get, errMsg } from "@/lib/api";
import { cn } from "@/lib/utils";

const OPTIONS = [["hire", "Hire", "border-success text-success"], ["hold", "Hold", "border-caution text-caution"], ["reject", "Reject", "border-risk text-risk"]];

export function DecisionDialog({ app, name, open, onOpenChange, onDecided, jobStatus, initial }) {
  const closed = ["closed", "archived"].includes(jobStatus);
  const [ackClosed, setAckClosed] = useState(false);
  const { data: reasons } = useQuery({ queryKey: ["decision-reasons"], queryFn: () => get("/decision-reasons"), staleTime: Infinity });
  const [decision, setDecision] = useState(initial || null);
  const [code, setCode] = useState("");
  const [text, setText] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const ready = decision && code && (code !== "Other" || text.trim().length >= 5) && (!closed || ackClosed);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/applications/${app.id}/decision`, { decision, reason_code: code, reason_text: text, confirm_closed_job: closed && ackClosed });
      toast.success("Decision recorded");
      onOpenChange(false);
      onDecided?.(data);
    } catch (e) { toast.error(errMsg(e)); setConfirm(false); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-[6px]" data-testid="decision-dialog">
        <DialogHeader><DialogTitle>{confirm ? "Confirm decision" : `Decide on ${name}`}</DialogTitle>
          <DialogDescription>{confirm ? "This is recorded in the audit log. Nothing is sent to the candidate until you confirm an email." : "A person makes every decision. A reason is required."}</DialogDescription></DialogHeader>
        {confirm ? (
          <div className="rounded-[6px] border border-line bg-subtle p-4 text-sm" data-testid="decision-confirm-summary">
            <p><span className="text-ink-muted">Decision:</span> <strong className="capitalize">{decision}</strong></p>
            <p><span className="text-ink-muted">Reason:</span> {code}{text && ` — ${text}`}</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-2">{OPTIONS.map(([k, l, tone]) => (
              <button key={k} type="button" onClick={() => { setDecision(k); setCode(""); }} data-testid={`decision-option-${k}`}
                className={cn("rounded-[5px] border px-3 py-2.5 text-sm font-medium transition-colors duration-150", decision === k ? `${tone} bg-white shadow-sm` : "border-line text-ink-secondary hover:bg-subtle")}>{l}</button>
            ))}</div>
            {decision && <select value={code} onChange={(e) => setCode(e.target.value)} className="h-9 w-full rounded-lg border border-line bg-white px-2 text-sm" data-testid="decision-reason-select">
              <option value="">Pick a reason…</option>{(reasons?.[decision] || []).map((r) => <option key={r} value={r}>{r}</option>)}</select>}
            {closed && <label className="flex items-start gap-2 rounded-[5px] border border-[#E8D7B0] bg-caution-tint p-3 text-sm text-caution" data-testid="decision-closed-warning">
              <input type="checkbox" checked={ackClosed} onChange={(e) => setAckClosed(e.target.checked)} className="mt-1" data-testid="decision-closed-confirm" />
              <span>This job is {jobStatus}. I confirm I still want to record a decision.</span></label>}
            <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={code === "Other" ? "Describe the reason (required)" : "Add detail (optional)"} data-testid="decision-reason-text" />
          </div>
        )}
        <DialogFooter className="gap-2">
          {confirm ? <><Button variant="ghost" onClick={() => setConfirm(false)} data-testid="decision-back-btn">Back</Button><Button onClick={submit} disabled={busy} data-testid="decision-confirm-btn">Confirm decision</Button></>
            : <Button disabled={!ready} onClick={() => setConfirm(true)} data-testid="decision-continue-btn">Continue</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
